import { useApolloClient } from '@apollo/client'
import axios from 'axios'
import exifr from 'exifr'
import { getAuth } from 'firebase/auth'
import { heicTo } from 'heic-to'
import { useMatch, useParams } from 'react-router-dom'
import { v4 } from 'uuid'
import config, { baseDomain, s3Media } from '../config'
import { BucketAlbumFragmentFragmentDoc, BucketFragmentFragmentDoc } from '../graphql/generated'
import logger from '../utils/logger'
import Semaphore from '../utils/semaphore'
import useGAEvent from '../views/auth/hooks/useGAEvent'
import useAlerts from '../views/buckets/hooks/useAlerts'
import useUploadsInProgress from '../views/buckets/hooks/useUploadsInProgress'
import { videoPlaceholder } from '../views/buckets/utils/placeholders'

const VIDEO_PLACEHOLDER = `${baseDomain}/${videoPlaceholder}`

// Create a single semaphore instance to control concurrency across all hook instances.
const fileProcessingSemaphore = new Semaphore(s3Media.maxConcurrentUploads)

interface UploadRequest {
  requestId: string
  file: File
  processedFile: File | null
  localImageFileUri: string
}

/**
 * Response structure from the media service after initiating an upload.
 */
interface MediaServiceResponse {
  id: string
  originalFilename: string
  fileS3Key: string
  filename: string
  bucketId: string
  status: string
  failureReason?: string
  createdAt: string
  updatedAt: string
  externalId?: string
  userId: string
  description?: string
  title?: string
  uploadUrls: {
    id: string
    chunkSize: number
    urls: string[]
  }
  livePhoto?: {
    filename: string
    fileSize: number
    fileS3Key: string
    uploadUrls: {
      id: string
      chunkSize: number
      urls: string[]
    }
  }
  metadata: {
    dateTaken: string
    contentType: string
    fileSize: number
    width?: number
    height?: number
    originalFilename: string
    thumbnail?: {
      hash: string
    }
  }
  source: string
}

/**
 * EXIF data extracted from an image file.
 */
interface ExifData {
  width?: number
  height?: number
  dateTaken: string
}

/**
 * Represents a single part of a multipart upload.
 */
interface UploadPart {
  partNumber: number
  eTag: string
}

/**
 * Response structure for polling media status.
 */
interface MediaStatusResponse {
  id: string
  status: string
  title: string
  filename: string
  originalFilename: string
  metadata: {
    file: {
      fileSize: number
      width: number
      height: number
      contentType: string
      dateTaken: string
      originalFilename: string
    }
    videoMeta: {}
  }
  thumbnailImage: {
    url: string
    width: number
    height: number
    size: number
    isLandscape: boolean
  }
  createdAt: string
  updatedAt: string
  bucketId: string
  userId: string
  isBanned: boolean
  fileS3Key: string
  source: string
}

/**
 * Implements the upload logic for media files, supporting both the new and legacy upload APIs.
 */
function useUploadImplementation() {
  const client = useApolloClient()
  const { createAlert } = useAlerts()
  const { beginTrackingQueued, beginTrackingProgress, updateStatus, endTrackingProgress, markUploadComplete } = useUploadsInProgress()
  const { trackEvent } = useGAEvent()
  const params = useParams<{ bucketId: string; albumId: string }>()
  const path = useMatch('/bucket/:bucketId/album/:albumId')
  const bucketId = params.bucketId ?? path?.params.bucketId
  const albumId = params.albumId ?? path?.params.albumId
  const userId = getAuth().currentUser?.uid

  /**
   * Extracts EXIF data (dimensions and date taken) from an image file.
   * Falls back to using the image's natural dimensions if EXIF is unavailable.
   * @param file The image file to extract EXIF from.
   */
  const extractExif = async (file: File, imageUrl: string): Promise<ExifData> => {
    const skipExif = ['gif', 'webp', 'tiff', 'tif'].includes((file.type.split('/')[1] || '').toLowerCase())
    // If we want to skip EXIF, just return a minimal ExifData with current date
    if (skipExif) {
      return { dateTaken: new Date().toISOString() }
    }

    const exifData = await exifr.parse(file)
    const { ExifImageHeight, ExifImageWidth, CreateDate } = exifData || {}
    const dateTaken = CreateDate ? new Date(CreateDate).toISOString() : new Date().toISOString()

    if (ExifImageWidth && ExifImageHeight) {
      return { width: ExifImageWidth, height: ExifImageHeight, dateTaken }
    }

    // If EXIF dimensions are not available, use the image's natural dimensions.
    const img = new Image()

    try {
      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => {
          resolve({
            width: img.naturalWidth || 1,
            height: img.naturalHeight || 1
          })
        }
        img.onerror = reject
        img.src = imageUrl
      })
      return { ...dimensions, dateTaken }
    } catch (error) {
      logger.error('Failed to extract EXIF:', error)
      // If all else fails, just return current date
      return { dateTaken: new Date().toISOString() }
    }
  }

  /**
   * Polls the media service for the status of a media item until it becomes ACTIVE or FAILED,
   * or until a maximum number of attempts is reached.
   * Updates Apollo cache and upload status accordingly.
   * @param mediaId The ID of the media to poll.
   */
  const pollMediaStatus = (mediaId: string, requestId: string, localImageUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const maxAttempts = 15
      const pollInterval = 2000
      let attempts = 0
      let pollTimeoutId: NodeJS.Timeout | null = null
      let authToken: string | null = null

      /**
       * Executes a single poll attempt for the media status.
       */
      const executePoll = async (): Promise<void> => {
        if (!authToken) {
          logger.error('Auth token missing during poll execution')
          return reject(new Error('Auth token missing during poll execution'))
        }

        if (attempts >= maxAttempts) {
          logger.error(`Polling timed out for mediaId: ${mediaId} after ${maxAttempts} attempts.`)
          updateStatus(requestId, 'ERROR')
          if (pollTimeoutId) clearTimeout(pollTimeoutId)
          return reject(new Error(`Polling timed out for mediaId: ${mediaId}`))
        }

        attempts += 1

        try {
          const response = await axios.get<MediaStatusResponse>(`${config.api.mediaService}/${mediaId}`, {
            headers: {
              Authorization: `${authToken}`,
              'x-client-name': config.app.name,
              'x-client-version': import.meta.env.REACT_APP_VERSION || '1.0.0'
            }
          })

          const { status, metadata, title, fileS3Key } = response.data as MediaStatusResponse & {
            metadata: { file: { width: number; height: number; fileSize: number; isLandscape: boolean } }
          }

          if (status === 'ACTIVE') {
            if (pollTimeoutId) clearTimeout(pollTimeoutId)
            new Promise<void>((_resolve, _reject) => {
              markUploadComplete(bucketId as string, requestId, {
                id: mediaId,
                albumId,
                createdAt: new Date().toISOString(),
                dateTaken: metadata.file.dateTaken ?? null,
                description: '',
                fileSize: metadata.file.fileSize,
                filename: metadata.file.originalFilename,
                height: metadata.file.height,
                imageUrl: `${s3Media.domain}/${fileS3Key}`,
                isBanned: false,
                isFavorite: false,
                isVideo: (metadata.videoMeta && Object.keys(metadata.videoMeta).length > 0) ?? false,
                mediaType: metadata.file.contentType,
                originalFilename: metadata.file.originalFilename,
                scheduledDeletionAt: null,
                title: title ?? '',
                userId,
                userTags: [],
                width: metadata.file.width
              })
            }).then(() => {
              // Revoke the object URL created for the preview if it wasn't the video placeholder
              if (!metadata.file.contentType.includes('video') && localImageUrl) {
                URL.revokeObjectURL(localImageUrl)
              }
            })
            return resolve()
          }

          if (status === 'FAILED') {
            logger.error(`Status became FAILED for mediaId: ${mediaId} on attempt ${attempts}.`)
            updateStatus(requestId, 'ERROR')
            if (pollTimeoutId) clearTimeout(pollTimeoutId)
            return reject(new Error(`Polling failed for mediaId: ${mediaId}`))
          }

          pollTimeoutId = setTimeout(executePoll, pollInterval)
        } catch (error) {
          logger.error(`Polling error for mediaId ${mediaId} on attempt ${attempts}:`, error)

          if (axios.isAxiosError(error) && error.response?.status === 404) {
            logger.warn(`Media ID ${mediaId} not found yet. This may be normal during processing.`)
          }

          pollTimeoutId = setTimeout(executePoll, pollInterval)
        }
        return undefined
      }

      // Obtain authentication token and start polling.
      getAuth()
        .currentUser?.getIdToken()
        .then((token) => {
          if (!token) {
            logger.error('Authentication token not available for polling')
            return reject(new Error('Authentication token not available for polling'))
          }
          authToken = token
          pollTimeoutId = setTimeout(executePoll, pollInterval)
          return undefined
        })
        .catch((tokenError) => {
          logger.error('Failed to get authentication token for polling:', tokenError)
          reject(new Error('Failed to get authentication token for polling'))
        })
    })
  }

  /**
   * Uploads a single file to the media service using multipart upload.
   * Handles chunking, progress tracking, error handling, and Apollo cache updates.
   * @param requestId The pre-generated request ID for tracking.
   * @param file The file to upload.
   * @param localImageFileUri The local image file URI for preview. This can be generated using URL.createObjectURL().
   */
  const uploadFile = async (requestId: string, file: File, localImageFileUri: string): Promise<string> => {
    if (!file || !bucketId || !userId) {
      throw new Error('Missing required upload parameters')
    }

    // Controls the number of concurrent chunk uploads for this file.
    const chunkUploadSemaphore = new Semaphore(s3Media.maxConcurrentChunkUploads)

    let mediaType = file.type
    const originalFilename = file.name
    const fileSize = file.size
    const fileExt = originalFilename.split('.').pop()?.toLowerCase()
    if (!mediaType && fileExt) {
      logger.warn(`File type empty for ${originalFilename}. Attempting fallback based on extension: ${fileExt}`)
      switch (fileExt) {
        case 'heic':
          mediaType = 'image/heic'
          break
        case 'heif':
          mediaType = 'image/heif'
          break
        case 'vob':
          mediaType = 'video/vob'
          break
        case 'tiff':
        case 'tif':
          mediaType = 'image/tiff'
          break
        default:
          logger.warn(`Unknown extension '${fileExt}' for file with empty type: ${originalFilename}. Cannot determine mediaType.`)
          break
      }
    }

    if (!mediaType) {
      logger.error(`Failed to determine mediaType for ${originalFilename}. Upload aborted.`)
      createAlert(`Could not determine file type for ${originalFilename}. Upload failed.`)
      throw new Error(`Could not determine mediaType for ${originalFilename}`)
    }

    const isVideo = mediaType.toLowerCase().includes('video')

    const splitName = originalFilename.split('.')
    splitName.pop()
    const title = splitName.join('.')
    // If this is a HEIC file, convert it to JPEG for browser compatibility and preview
    if (fileExt === 'heic' || fileExt === 'heif') {
      mediaType = 'image/jpeg'
    }

    // Extract EXIF data for images, or use current date for videos.
    const exifData = !isVideo ? await extractExif(file, localImageFileUri) : { dateTaken: new Date().toISOString() }
    // Begin tracking upload progress with the pre-generated requestId.
    beginTrackingProgress(bucketId, requestId, {
      albumId,
      createdAt: new Date().toISOString(),
      dateTaken: exifData.dateTaken,
      description: '',
      fileSize,
      filename: originalFilename,
      height: 0,
      imageUrl: localImageFileUri,
      isBanned: false,
      isFavorite: false,
      isRestricted: false,
      isVideo,
      mediaType,
      originalFilename,
      scheduledDeletionAt: null,
      title,
      userId,
      userTags: [],
      width: 0
    })
    // Perform the upload in the background.
    return (async () => {
      const token = await getAuth().currentUser?.getIdToken()
      if (!token) {
        throw new Error('User token not available for upload.')
      }
      try {
        // Initiate the upload and get chunk URLs.
        const { data } = await axios.post<MediaServiceResponse>(
          `${config.api.mediaService}`,
          {
            bucketId,
            metadata: {
              dateTaken: exifData.dateTaken,
              contentType: mediaType,
              fileSize,
              ...(exifData.width ? { width: exifData.width } : {}),
              ...(exifData.height ? { height: exifData.height } : {}),
              originalFilename
            },
            albumId,
            title
          },
          {
            headers: {
              Authorization: `${token}`,
              'x-client-name': config.app.name,
              'x-client-version': import.meta.env.REACT_APP_VERSION || '1.0.0',
              'x-request-id': requestId
            }
          }
        )

        const { id: mediaId, uploadUrls } = data as MediaServiceResponse
        const { chunkSize } = uploadUrls
        const numChunks = Math.ceil(fileSize / chunkSize)
        const parts: UploadPart[] = []
        let externalId: string | undefined

        // Upload each chunk, limiting concurrency with a semaphore.
        const uploadPromises = Array.from({ length: numChunks }, (_, i) => {
          return chunkUploadSemaphore.execute(async () => {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize, fileSize)
            const fileChunk = file.slice(start, end)

            try {
              const response = await axios.put(uploadUrls.urls[i], fileChunk)
              externalId = response.data.externalId
              parts[i] = {
                partNumber: i + 1,
                eTag: response.headers.etag
              }
              const completedChunks = parts.filter((p) => p).length
              updateStatus(requestId, Math.round((completedChunks / numChunks) * 100))
            } catch (error) {
              logger.error(`Failed to upload chunk ${i + 1}:`, error)
              throw error
            }
          })
        })

        // Wait for all chunk uploads to complete.
        const results = await Promise.allSettled(uploadPromises)

        // Check for failed chunk uploads.
        const failedChunks = results.filter((result) => result.status === 'rejected')

        if (failedChunks.length > 0) {
          failedChunks.forEach((failure, index) => {
            logger.error(`Chunk upload failed (promise index ${index}):`, (failure as PromiseRejectedResult).reason)
          })
          throw new Error(`Upload failed: ${failedChunks.length} out of ${numChunks} chunks could not be uploaded. See console for details.`)
        }

        // Ensure all parts are present before completing the upload.
        const finalParts = parts.filter((p) => p)
        if (finalParts.length !== numChunks) {
          throw new Error(`Upload integrity issue: Expected ${numChunks} parts after successful uploads, but found ${finalParts.length}.`)
        }

        // Complete the multipart upload.
        await axios.put(
          `${config.api.mediaService}/${mediaId}`,
          { s3UploadParts: finalParts },
          {
            headers: {
              Authorization: `${token}`,
              'x-client-name': config.app.name,
              'x-client-version': import.meta.env.REACT_APP_VERSION || '1.0.0',
              'x-request-id': externalId ?? requestId
            }
          }
        )

        await pollMediaStatus(mediaId, requestId, localImageFileUri)

        // Update Apollo cache counters after successful upload.
        client.cache.updateFragment(
          {
            id: `Bucket:${bucketId}`,
            fragment: BucketFragmentFragmentDoc
          },
          (current) => {
            if (!current) return null

            const totalMedia = current?.counters?.totalMedia ?? 0
            const totalMediaMinusTrash = current.counters?.totalMediaMinusTrash ?? 0
            const totalSize = current?.counters?.totalSize ?? 0

            return {
              ...current,
              counters: {
                ...current.counters,
                totalMedia: totalMedia + 1,
                totalMediaMinusTrash: totalMediaMinusTrash + 1,
                totalSize: totalSize + fileSize
              }
            }
          }
        )

        client.cache.updateFragment(
          {
            id: `BucketAlbum:${albumId}`,
            fragment: BucketAlbumFragmentFragmentDoc
          },
          (current) => {
            if (!current) return null

            const totalMedia = current?.counters?.totalMedia ?? 0

            return {
              ...current,
              counters: {
                ...current.counters,
                totalMedia: totalMedia + 1
              }
            }
          }
        )
        return mediaId
      } catch (error) {
        logger.error('Upload failed (inside background async):', error)
        if (axios.isAxiosError(error)) {
          logger.error('Response:', error.response?.data)
          logger.error('Request:', error.config)
        }
        updateStatus(requestId, 'ERROR')
        createAlert('Upload failed. Please try again.')
        throw error
      }
    })()
  }

  /**
   * Uploads a single file and tracks analytics.
   * @param file The file to upload.
   */
  const uploadSingle = async (file: File) => {
    const requestId = v4()
    let newMediaId: string | null = null
    try {
      trackEvent('media_upload_event', {
        metric: 'media_upload_event',
        id: userId,
        bucketId,
        albumId,
        isMultiple: false,
        platform: 'WEB'
      })
      let processedFile: File | null = null
      let localImageFileUri: string = ''

      if (bucketId && albumId && userId) {
        const originalFilename = file.name
        // const fileSize = file.size
        let mediaType = file.type
        const fileExt = originalFilename.split('.').pop()?.toLowerCase()
        if (!mediaType && fileExt) {
          // Basic type guessing for optimistic update
          switch (fileExt) {
            case 'heic':
              mediaType = 'image/heic'
              break
            case 'heif':
              mediaType = 'image/heif'
              break
            case 'vob':
              mediaType = 'video/vob'
              break
            case 'tiff':
            case 'tif':
              mediaType = 'image/tiff'
              break
            default:
              logger.warn(`Unknown extension '${fileExt}' for file with empty type: ${originalFilename}. Cannot determine mediaType.`)
              break
          }
        }
        const needsConversion = ['image/heic', 'image/heif'].includes(mediaType)
        const isVideo = mediaType?.toLowerCase().includes('video') ?? false
        processedFile = file
        if (needsConversion) {
          try {
            const heicBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.5 })
            processedFile = heicBlob as File
          } catch (err) {
            logger.error('Failed to convert HEIC to JPEG:', err)
          }
        }
        localImageFileUri = isVideo ? VIDEO_PLACEHOLDER : URL.createObjectURL(processedFile)
        const splitName = originalFilename.split('.')
        splitName.pop()
        const title = splitName.join('.')

        const stagedImage = {
          __typename: 'BucketMedia' as const,
          id: requestId, // Use the generated ID
          albumId,
          bucketId,
          createdAt: new Date().toISOString(), // Use current time optimistically
          dateTaken: new Date().toISOString(), // Use current time optimistically
          description: '',
          fileSize: file.size,
          filename: originalFilename,
          height: 0,
          imageUrl: localImageFileUri,
          isBanned: false,
          isFavorite: false,
          isMobile: false,
          isRestricted: false,
          isVideo,
          legacyFilePath: null,
          livePhoto: null,
          mediaType: mediaType || '',
          moderationStatus: null,
          originalFilename: originalFilename || '',
          scheduledDeletionAt: null,
          signedUrl: null,
          title: title || '',
          userId,
          userTags: [],
          width: 0
        }

        beginTrackingQueued(bucketId, requestId, stagedImage)
      }

      newMediaId = await uploadFile(requestId, file, localImageFileUri)
    } catch (error) {
      logger.error('Upload failed:', error)
      updateStatus(requestId, 'ERROR')
      createAlert('Upload failed. Please try again.')
      throw error
    } finally {
      endTrackingProgress(requestId)
    }
    return newMediaId
  }

  /**
   * Uploads multiple files concurrently, limiting the number of simultaneous uploads.
   * Tracks analytics and refetches queries after completion.
   * @param files The files to upload.
   */
  const uploadMultiple = async (files: File[]) => {
    // Store context locally in case user navigates away during upload.
    const currentBucketId = bucketId
    const currentAlbumId = albumId
    const currentUserId = userId

    const objectUrlsToRevoke: { [key: string]: string } = {}
    let uploadRequests: UploadRequest[] = []
    if (currentBucketId && currentUserId) {
      const promises: Promise<UploadRequest>[] = files.map(async (file: File) => {
        const currentRequestId = v4()
        const originalFilename = file.name
        const fileSize = file.size
        let mediaType = file.type
        const fileExt = originalFilename.split('.').pop()?.toLowerCase()
        let localImageFileUri = ''
        if (!mediaType && fileExt) {
          switch (fileExt) {
            case 'heic':
              mediaType = 'image/heic'
              break
            case 'heif':
              mediaType = 'image/heif'
              break
            case 'vob':
              mediaType = 'video/vob'
              break
            case 'tiff':
            case 'tif':
              mediaType = 'image/tiff'
              break
            default:
              logger.warn(`Unknown extension '${fileExt}' for file with empty type: ${originalFilename}. Cannot determine mediaType.`)
              break
          }
        }
        const needsConversion = ['image/heic', 'image/heif'].includes(mediaType)
        let processedFile = file
        if (needsConversion) {
          try {
            const heicBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.5 })
            processedFile = heicBlob as File
          } catch (err) {
            logger.error('Failed to convert HEIC to JPEG:', err)
          }
        }

        const isVideo = mediaType?.toLowerCase().includes('video') ?? false
        localImageFileUri = isVideo ? VIDEO_PLACEHOLDER : URL.createObjectURL(processedFile)
        if (!isVideo) {
          objectUrlsToRevoke[currentRequestId] = localImageFileUri
        }
        const splitName = originalFilename.split('.')
        splitName.pop()
        const title = splitName.join('.')

        const stagedImage = {
          __typename: 'BucketMedia' as const,
          id: currentRequestId,
          albumId: currentAlbumId ?? '',
          bucketId: currentBucketId,
          createdAt: new Date().toISOString(),
          dateTaken: new Date().toISOString(),
          description: '',
          fileSize,
          filename: originalFilename,
          height: 0,
          imageUrl: localImageFileUri,
          isBanned: false,
          isFavorite: false,
          isMobile: false,
          isRestricted: false,
          isVideo,
          legacyFilePath: null,
          livePhoto: null,
          mediaType: mediaType || '',
          moderationStatus: null,
          originalFilename: originalFilename || '',
          scheduledDeletionAt: null,
          signedUrl: null,
          title: title || '',
          userId: currentUserId,
          userTags: [],
          width: 0
        }
        beginTrackingQueued(currentBucketId, currentRequestId, stagedImage)
        return { requestId: currentRequestId, file, processedFile, localImageFileUri }
      })
      uploadRequests = await Promise.all(promises)
    }

    try {
      trackEvent('media_upload_event', {
        metric: 'media_upload_event',
        id: currentUserId,
        bucketId: currentBucketId,
        albumId: currentAlbumId,
        isMultiple: true,
        filesCount: files.length,
        platform: 'WEB'
      })

      const uploadPromises = uploadRequests.map(({ requestId, file, localImageFileUri }: UploadRequest) => {
        return fileProcessingSemaphore.execute(async () => {
          try {
            await uploadFile(requestId, file, localImageFileUri) // Pass the requestId
            return { status: 'fulfilled', requestId, filename: file.name }
          } catch (error) {
            logger.error(`Failed to upload file ${file.name} within semaphore execute:`, error)
            updateStatus(requestId, 'ERROR') // Ensure status update on error
            createAlert(`Upload failed for ${file.name}.`)
            // Don't re-throw here, let Promise.allSettled handle it
            return { status: 'rejected', requestId, filename: file.name, reason: error }
          }
        })
      })

      const settledResults = await Promise.allSettled(uploadPromises)

      settledResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value?.status === 'fulfilled') {
          endTrackingProgress(result.value.requestId)
          delete objectUrlsToRevoke[result.value.requestId]
        } else if (result.status === 'fulfilled' && result.value?.status === 'rejected') {
          // Error already logged and alerted inside the semaphore execute catch block
          logger.error(`Upload failed for ${result.value.filename}:`, result.value.reason)
        } else if (result.status === 'rejected') {
          // This catches errors in semaphore execution itself, unlikely but possible
          const file = files[index]
          const { requestId } = result.reason
          if (requestId) updateStatus(requestId, 'ERROR')
          logger.error(`Semaphore execution failed for file index ${index} (${file?.name}):`, result.reason)
          createAlert(`Upload failed for ${file?.name || 'unknown file'}.`)
        }
      })
    } catch (error) {
      // This outer catch likely won't be hit often due to inner catches, but good for safety
      logger.error('Batch upload failed (outer catch): ', error)
      createAlert('An unexpected error occurred during batch upload. Some files may have failed.')
      // Attempt to mark all remaining pending uploads as errored? Difficult without tracking state here.
    }
  }

  return { uploadSingle, uploadMultiple }
}

/**
 * Hook to provide media upload functionality, switching between new and legacy APIs based on feature flag.
 */
export default function useMediaServiceUpload() {
  return useUploadImplementation()
}
